/** Steel streams the page together with its browser chrome. Capture markers
 * therefore render INSIDE the native reviewed document, never over the iframe.
 * SSE carries selections/issue IDs; the existing authorized command channel
 * sends saved pins back to the bridge after load, save and navigation. */
import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useState } from 'react';
import { deriveIssueTitle, pinNumbersByCreation, pageGroupKey, type IssueTitleCaptureContext, type IssueTitleElementData } from '@overlayqa/shared';
import { CaptureToolbar } from '@overlayqa/shared/ui';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { useAuth } from '@/lib/auth';
import { useDraftExitFeedback } from '@/hooks/useDraftExitFeedback';
import { IssueDrawer, type IssueDraftFields } from '../issues/IssueDrawer';
import {
  getStreamTicket,
  listLiveReviewIssues,
  getLiveReviewIssue,
  type LiveReviewPinnedIssue,
  buildStreamUrl,
  createLiveReviewIssue,
  sendReviewCommand,
  sendReviewHeartbeat,
  endLiveReview,
  type LiveReviewElementData,
} from '@/lib/api/liveReviewStream';
import { fitRemoteViewport, type BridgeRect, type RenderedSize, type RemoteViewport } from '@/lib/coordinateScale';
import { log } from '@/lib/logger';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Bridge click payload shape from reviewBridgeScript.ts getFullData().
 * Carries full element data including designMetadata for issue creation.
 */
interface BridgeClickPayload {
  type: 'OQ_BRIDGE_CLICK';
  rect: BridgeRect;
  documentDimensions?: { width: number; height: number };
  viewport?: { width: number; height: number };
  scrollOffset?: { x: number; y: number };
  selector: string;
  tagName: string;
  className: string;
  altText?: string;
  titleContext?: IssueTitleCaptureContext;
  fingerprint?: IssueTitleElementData['fingerprint'];
  boundingRect: { x: number; y: number; width: number; height: number };
  computedStyles?: Record<string, string>;
  designMetadata: Record<string, unknown>;
}

function isBridgeClickPayload(v: unknown): v is BridgeClickPayload {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    o.type === 'OQ_BRIDGE_CLICK' &&
    typeof o.selector === 'string' &&
    typeof o.tagName === 'string' &&
    o.designMetadata !== null &&
    typeof o.designMetadata === 'object'
  );
}

/** Confirmation state for a successfully created issue. */
interface CreatedIssueConfirmation {
  selector: string;
  issueId: string;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface InteractiveReviewProps {
  liveViewUrl: string;
  pageUrl?: string;
  status?: 'starting' | 'live' | 'ended';
  title?: string;
  onClose?: () => void;

  /** When set, opens an SSE stream for bridge events on mount. */
  reviewId?: string;
  /** Required alongside reviewId to issue the stream ticket. */
  projectId?: string;
  /**
   * Remote browser viewport dimensions (as launched at Steel session creation).
   * Defaults to 1280x800. Sets the fixed viewer CSS size and fallback capture coordinate dimensions.
   */
  remoteViewport?: RemoteViewport;
  /** Override the measured available container for layout tests. */
  renderedSize?: RenderedSize;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<NonNullable<InteractiveReviewProps['status']>, string> = {
  starting: 'Connecting...',
  live: 'Live',
  ended: 'Ended',
};

const STATUS_CLASSES: Record<NonNullable<InteractiveReviewProps['status']>, string> = {
  starting: 'bg-yellow-100 text-yellow-800',
  live: 'bg-green-100 text-green-800',
  ended: 'bg-gray-100 text-gray-600',
};

const DEFAULT_REMOTE_VIEWPORT: RemoteViewport = { width: 1280, height: 800 };

// ─── Component ───────────────────────────────────────────────────────────────

export function InteractiveReview({
  liveViewUrl,
  pageUrl = '',
  status = 'starting',
  title,
  onClose,
  reviewId,
  projectId,
  remoteViewport = DEFAULT_REMOTE_VIEWPORT,
  renderedSize,
}: InteractiveReviewProps) {
  const { getToken } = useAuth();
  const exitFeedback = useDraftExitFeedback(projectId);
  const statusLabel = STATUS_LABELS[status];
  const statusClass = STATUS_CLASSES[status];
  const viewerContainer = useRef<HTMLDivElement>(null);
  const [measuredSize, setMeasuredSize] = useState<RenderedSize>({ width: 0, height: 0 });
  const viewerFit = fitRemoteViewport(renderedSize ?? measuredSize, remoteViewport);
  useLayoutEffect(() => {
    if (renderedSize || !viewerContainer.current) return;
    const container = viewerContainer.current;
    const observer = new ResizeObserver(entries => {
      for (const { contentRect } of entries) setMeasuredSize({ width: contentRect.width, height: contentRect.height });
    });
    observer.observe(container);
    const initial = container.getBoundingClientRect();
    setMeasuredSize({ width: initial.width, height: initial.height });
    return () => observer.disconnect();
  }, [renderedSize]);

  const [savedIssues, setSavedIssues] = useState<LiveReviewPinnedIssue[]>([]);
  const [editingIssue, setEditingIssue] = useState<LiveReviewPinnedIssue | null>(null);
  const issueSelectionRequest = useRef(0);
  useEffect(() => () => { issueSelectionRequest.current++; }, []);
  const [pageRevision, setPageRevision] = useState(0);
  const authRef = useRef(getToken); authRef.current = getToken;
  const [issueDraft, setIssueDraft] = useState<BridgeClickPayload | null>(null);
  const [currentPageUrl, setCurrentPageUrl] = useState(pageUrl);
  const [captureMode, setCaptureMode] = useState<'idle' | 'select'>('idle');
  const [capturePaused, setCapturePaused] = useState(false);
  const [commandPending, setCommandPending] = useState(false);
  const commandInFlight = useRef(false);
  const changeCapture = useCallback(async (mode: 'idle' | 'select', paused = false) => {
    if (!projectId || !reviewId || commandInFlight.current) return;
    commandInFlight.current = true; setCommandPending(true);
    try {
      const token = await getToken();
      await sendReviewCommand(token ?? undefined, projectId, reviewId, { type: 'OQ_BRIDGE_SET_MODE', capture: mode === 'select' && !paused });
      setCaptureMode(mode); setCapturePaused(paused);
    } catch {
      toast.error('Capture mode did not change. Try again.');
    } finally { commandInFlight.current = false; setCommandPending(false); }
  }, [projectId, reviewId, getToken]);
  const closeReview = async () => {
    if (commandInFlight.current) return;
    issueSelectionRequest.current++;
    commandInFlight.current = true; setCommandPending(true);
    try {
      if (projectId && reviewId) await endLiveReview((await getToken()) ?? undefined, projectId, reviewId);
      onClose?.();
    } catch { toast.error('The review could not close. Try again.'); }
    finally { commandInFlight.current = false; setCommandPending(false); }
  };
  useEffect(() => {
    if (captureMode === 'idle' || issueDraft || editingIssue || exitFeedback.question) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault(); void changeCapture('idle');
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [captureMode, issueDraft, editingIssue, exitFeedback.question, changeCapture]);
  useEffect(() => {
    if (!exitFeedback.question || issueDraft) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      exitFeedback.skip();
    };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  }, [exitFeedback.question, exitFeedback.skip, issueDraft]);

  const [createdIssue, setCreatedIssue] = useState<CreatedIssueConfirmation | null>(null);
  const [createFailed, setCreateFailed] = useState<{ selector: string } | null>(null);
  const savedIssuesRef = useRef(savedIssues); savedIssuesRef.current = savedIssues;
  const pinCommands = useRef<Promise<void>>(Promise.resolve());
  const loadPins = useCallback(async () => {
    if (!projectId) return;
    const startedWith = new Set(savedIssuesRef.current.map(issue => issue.id));
    const pins = await listLiveReviewIssues((await authRef.current()) ?? undefined, projectId);
    setSavedIssues(current => [...pins, ...current.filter(issue => !startedWith.has(issue.id) && !pins.some(pin => pin.id === issue.id))]);
  }, [projectId]);
  useEffect(() => { void loadPins().catch(() => toast.error('Saved pins could not load. Reopen the review to try again.')); }, [loadPins]);
  const pageIssues = savedIssues.filter(issue => pageGroupKey(issue.pageUrl) === pageGroupKey(currentPageUrl));
  useEffect(() => {
    if (!projectId || !reviewId) return;
    const numbers = pinNumbersByCreation(savedIssues.map(issue => ({ id: issue.id, createdAt: String(issue.createdAt) })));
    const pins = savedIssues.filter(issue => pageGroupKey(issue.pageUrl) === pageGroupKey(currentPageUrl)).map(issue => ({
      id: issue.id, selector: issue.elementData.selector, title: issue.title, number: numbers.get(issue.id)!,
    }));
    pinCommands.current = pinCommands.current.catch(() => {}).then(async () => {
      await sendReviewCommand((await authRef.current()) ?? undefined, projectId, reviewId, { type: 'OQ_BRIDGE_CAPTURE_PINS', pins });
    }).catch(() => { toast.error('Saved pins could not display. Reopen the review to try again.'); });
  }, [savedIssues, currentPageUrl, pageRevision, projectId, reviewId]);

  // ─── Handle OQ_BRIDGE_CLICK: create a live-review issue ─────────────────
  const handleBridgeClick = useCallback(
    (payload: BridgeClickPayload) => {
      if (!projectId) return;

      // Clear any previous confirmation before creating a new one.
      setCreatedIssue(null);
      setCreateFailed(null);
      issueSelectionRequest.current++;
      setEditingIssue(null);

      // A click selects evidence; only composing and submitting creates an issue.
      setIssueDraft(payload);
    },
    [projectId],
  );

  const submitDraft = async (fields: IssueDraftFields) => {
    if (!issueDraft || !projectId) return;
    const payload = issueDraft;
    const elementData: LiveReviewElementData = {
      selector: payload.selector, tagName: payload.tagName, className: payload.className,
      altText: payload.altText, boundingRect: payload.boundingRect, designMetadata: payload.designMetadata,
      titleContext: payload.titleContext, fingerprint: payload.fingerprint,
    };
    try {
      const token = await getToken();
      const issue = await createLiveReviewIssue(token ?? undefined, projectId, {
        projectId, ...fields, title: deriveIssueTitle({ title: fields.title, elementData, pageTitle: title }), source: 'team-review', elementData,
        pinX: ((payload.boundingRect.x + payload.boundingRect.width / 2) / (payload.documentDimensions?.width || remoteViewport.width)) * 100,
        pinY: ((payload.boundingRect.y + payload.boundingRect.height / 2) / (payload.documentDimensions?.height || remoteViewport.height)) * 100,
        pinType: 'element', documentDimensionsAtCapture: payload.documentDimensions,
        viewportAtCapture: payload.viewport, scrollOffsetAtCapture: payload.scrollOffset,
        screenshotUrl: '', pageUrl: currentPageUrl,
      });
      setSavedIssues(current => [...current.filter(saved => saved.id !== issue.id), { ...issue, createdAt: issue.createdAt || new Date(), ...fields, title: deriveIssueTitle({ title: fields.title, elementData, pageTitle: title }), elementData, pageUrl: currentPageUrl }]);
      setCreatedIssue({ selector: payload.selector, issueId: issue.id });
    } catch (error) {
      setCreateFailed({ selector: payload.selector });
      throw error;
    }
  };

  const handleBridgePayload = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') return;
    const event = payload as Record<string, unknown>;
    if (event.type === 'OQ_PAGE_LOADED') {
      if (typeof event.url === 'string' && /^https?:\/\//.test(event.url)) setCurrentPageUrl(event.url);
      setPageRevision(value => value + 1);
      setCaptureMode('idle'); setCapturePaused(false);
    } else if (event.type === 'OQ_BRIDGE_PIN_SELECTED' && typeof event.issueId === 'string') {
      const saved = savedIssues.find(issue => issue.id === event.issueId);
      if (saved) {
        const selection = ++issueSelectionRequest.current;
        // A just-saved edit can precede the background pin-list refresh.
        // Read the saved row for this opening; never replace an open draft
        // when unrelated list refreshes finish later.
        void authRef.current().then(token => getLiveReviewIssue(token ?? undefined, saved.id)).then(issue => {
          if (selection === issueSelectionRequest.current && issue.id === saved.id && issue.projectId === projectId) setEditingIssue(issue);
        }).catch(() => { if (selection === issueSelectionRequest.current) toast.error('The issue could not open. Select its pin to try again.'); });
      }
    } else if (isBridgeClickPayload(payload)) handleBridgeClick(payload);
  };
  // Keep one SSE connection while its handler reads current page/issue state.
  const bridgeHandlerRef = useRef(handleBridgePayload); bridgeHandlerRef.current = handleBridgePayload;

  // ─── SSE client lifecycle ─────────────────────────────────────────────────
  useEffect(() => {
    if (!reviewId || !projectId) return;

    let es: EventSource | null = null;
    let cancelled = false;

    const open = async () => {
      try {
        const token = await getToken();
        if (cancelled) return;

        const ticket = await getStreamTicket(token ?? undefined, projectId, reviewId);
        if (cancelled) return;

        const url = buildStreamUrl(reviewId, ticket);
        log.info('[InteractiveReview] opening SSE stream', { reviewId });

        es = new EventSource(url);

        // Named event: `event: review_event\ndata: <json>\n\n`
        // EventSource dispatches named events when addEventListener is used.
        const onReviewEvent = (e: MessageEvent) => {
          try {
            const payload = JSON.parse(e.data) as unknown;
            bridgeHandlerRef.current(payload);
          } catch (err) {
            log.warn('[InteractiveReview] failed to parse review_event', {
              error: err instanceof Error ? err.message : String(err),
            });
          }
        };

        es.addEventListener('review_event', onReviewEvent as EventListener);
      } catch (err) {
        log.warn('[InteractiveReview] SSE setup failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    };

    void open();

    return () => {
      cancelled = true;
      if (es) {
        log.info('[InteractiveReview] closing SSE stream', { reviewId });
        es.close();
      }
    };
  }, [reviewId, projectId]);

  // ─── Heartbeat: keep lastActivityAt fresh while the surface is open ───────
  // Fires every 30 s so the server-side reaper (P3) knows the session is alive.
  // Errors are swallowed — a transient network blip must not crash the session.
  useEffect(() => {
    if (!reviewId || !projectId) return;

    const id = setInterval(() => {
      const beat = async () => {
        try {
          const token = await getToken();
          await sendReviewHeartbeat(token ?? undefined, projectId, reviewId);
          log.info('[InteractiveReview] heartbeat sent', { reviewId });
        } catch (err) {
          log.warn('[InteractiveReview] heartbeat failed (ignored)', {
            error: err instanceof Error ? err.message : String(err),
          });
        }
      };
      void beat();
    }, 30_000);

    return () => clearInterval(id);
  }, [reviewId, projectId, getToken]);

  // ─── Render ───────────────────────────────────────────────────────────────



  return (
    <section className="flex flex-col w-full h-full min-h-0 bg-white rounded-xl overflow-hidden shadow-xl">
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {title && (
            <span className="text-sm font-medium text-gray-900 truncate">{title}</span>
          )}
          <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${statusClass}`}
            data-testid="interactive-review-status"
          >
            {statusLabel}
          </span>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={() => { void closeReview(); }}
            disabled={commandPending}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 shrink-0"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </header>

      <div ref={viewerContainer} data-testid="streamed-viewport-container" className="relative flex-1 min-h-0 overflow-hidden bg-gray-50">
        <iframe
          src={liveViewUrl}
          title="Team Review live session"
          className="absolute border-0"
          // These are measured page geometry, not design spacing. Keep the
          // provider app at the captured CSS size and scale its whole output.
          style={{ width: remoteViewport.width, height: remoteViewport.height, left: viewerFit.left, top: viewerFit.top,
            transform: `scale(${viewerFit.scale})`, transformOrigin: 'top left', visibility: viewerFit.scale > 0 ? 'visible' : 'hidden' }}
          allow="clipboard-read; clipboard-write"
          referrerPolicy="no-referrer"
        />

        {/*
          Issue-created confirmation badge.
          Shown after a successful OQ_BRIDGE_CLICK issue creation.
          Positioned at the top-right corner of the overlay area.
          LIVE-VERIFY: confirm the badge appears and the issue appears in the
          project issue list after a real bridge click through the Steel session.
        */}
        {createFailed && !createdIssue && (
          <div
            data-testid="issue-create-failed"
            className="absolute top-2 right-2 pointer-events-none"
            style={{ zIndex: 10 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium bg-red-100 text-red-800 shadow-sm">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                <path d="M6 0a6 6 0 100 12A6 6 0 006 0zm-.75 3h1.5v4h-1.5V3zm0 5h1.5v1.5h-1.5V8z" />
              </svg>
              The issue did not save. Try again in the form.
            </span>
          </div>
        )}
        {createdIssue && (
          <div
            data-testid="issue-created-confirmation"
            data-issue-id={createdIssue.issueId}
            className="absolute top-2 right-2 pointer-events-none"
            style={{ zIndex: 10 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800 shadow-sm">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                <path d="M10.28 2.28L4.5 8.06 1.72 5.28a1 1 0 00-1.44 1.44l3.5 3.5a1 1 0 001.44 0l6.5-6.5a1 1 0 00-1.44-1.44z" />
              </svg>
              Issue created
            </span>
          </div>
        )}
      </div>
      {projectId && reviewId && status === 'live' && <CaptureToolbar
        mode={captureMode} isPaused={capturePaused} isSaving={commandPending} count={pageIssues.length}
        hidden={!!issueDraft || !!editingIssue || !!exitFeedback.question} showSpacing={false}
        tools={{ selectLabel: 'Create', screenshot: false, onSelect: () => { void changeCapture('select'); } }}
        onTogglePause={() => { void changeCapture('select', !capturePaused); }}
        onDone={() => { void changeCapture('idle'); }} />}
      {issueDraft && projectId && <IssueDrawer isOpen projectId={projectId}
        captureTitle={deriveIssueTitle({ elementData: issueDraft, pageTitle: title })}
        onClose={() => setIssueDraft(current => current === issueDraft ? null : current)}
        onDismiss={() => {
          setIssueDraft(current => current === issueDraft ? null : current);
          exitFeedback.offer({ closedScreen: 'dashboard_new_issue', placement: 'floating' });
        }}
        onCreate={submitDraft} />}
      {editingIssue && projectId && <IssueDrawer isOpen projectId={projectId} issue={editingIssue}
        captureTitle={deriveIssueTitle(editingIssue)}
        onClose={() => { issueSelectionRequest.current++; setEditingIssue(null); void loadPins().catch(() => toast.error('Saved pins could not refresh. Reopen the review to try again.')); }} />}
      {exitFeedback.question && createPortal(exitFeedback.question, document.body)}
    </section>
  );
}
